<template>
  <div class="topup" :class="{ 'topup-dark': isDarkMode }">
    <p class="pay-lead">
      Pro dobití kreditu stačí poslat libovolnou částku na účet
      <span class="pay-lead-accent">{{ paymentData.BankAccount }}</span>
      s variabilním symbolem
      <span class="pay-lead-accent">{{ paymentData.UserVS }}</span>.
      Můžeš si taky vygenerovat QR pro platbu
      <button type="button" class="pay-lead-link" @click="focusAmount">zde</button>.
    </p>
    <div class="pay-grid">
        <div class="pay-chip">
          <div class="pay-value-row">
            <span class="pay-value pay-value-accent">{{ paymentData.BankAccount }}</span>
            <button
              type="button"
              class="copy-btn"
              :title="copiedKey === 'account' ? 'Zkopírováno' : 'Kopírovat číslo účtu'"
              @click="copyValue('account', paymentData.BankAccount)"
            >
              <fa-icon :icon="copiedKey === 'account' ? 'fa-solid fa-check' : 'fa-regular fa-copy'" />
            </button>
          </div>
          <span class="pay-label">Číslo účtu</span>
        </div>
        <div class="pay-chip pay-chip-vs">
          <div class="pay-value-row">
            <span class="pay-value pay-value-accent">{{ paymentData.UserVS }}</span>
            <button
              type="button"
              class="copy-btn"
              :title="copiedKey === 'vs' ? 'Zkopírováno' : 'Kopírovat VS'"
              @click="copyValue('vs', paymentData.UserVS)"
            >
              <fa-icon :icon="copiedKey === 'vs' ? 'fa-solid fa-check' : 'fa-regular fa-copy'" />
            </button>
          </div>
          <span class="pay-label">Variabilní symbol</span>
        </div>
        <div class="pay-chip pay-chip-iban">
          <span class="pay-value">{{ formattedIban }}</span>
          <span class="pay-label">IBAN</span>
        </div>
        <div class="pay-chip pay-chip-amount">
          <div class="pay-stepper">
            <button
              type="button"
              class="pay-stepper-btn pay-stepper-btn-jump"
              :disabled="amountNum <= jumpStep"
              :title="'−' + jumpStep"
              @click="nudge(-jumpStep)"
            >
              <fa-icon icon="fa-solid fa-minus" />
              <span class="jump-num">{{ jumpStep }}</span>
            </button>
            <button
              type="button"
              class="pay-stepper-btn"
              :disabled="amountNum <= minAmount"
              title="Méně"
              @click="nudge(-stepAmount)"
            >
              <fa-icon icon="fa-solid fa-minus" />
            </button>
            <input
              class="pay-amount-input"
              type="text"
              inputmode="decimal"
              ref="autofocus"
              placeholder="Částka"
              :value="creditAmount"
              @input="onAmountInput"
              @blur="onAmountBlur"
            />
            <button
              type="button"
              class="pay-stepper-btn"
              :disabled="amountNum >= maxAmount"
              title="Více"
              @click="nudge(stepAmount)"
            >
              <fa-icon icon="fa-solid fa-plus" />
            </button>
            <button
              type="button"
              class="pay-stepper-btn pay-stepper-btn-jump"
              :disabled="amountNum + jumpStep > maxAmount"
              :title="'+' + jumpStep"
              @click="nudge(jumpStep)"
            >
              <fa-icon icon="fa-solid fa-plus" />
              <span class="jump-num">{{ jumpStep }}</span>
            </button>
          </div>
          <span class="pay-label">Částka</span>
        </div>
      </div>

      <div class="qr-area">
        <qrcode-vue
          v-show="creditAmount !== null"
          :value="qrCodeValue"
          :size="200"
          level="H"
          :foreground="'#FF8A4C'"
          :background="qrBackground"
        />
        <p v-show="creditAmount === null" class="qr-hint">QR kód se zobrazí po zadání částky</p>
      </div>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted, computed } from "vue";
import { useStore } from "vuex";
import QrcodeVue from "qrcode.vue";

const props = defineProps({
  paymentData: {
    type: [Array, Object]
  }
});

const store = useStore();
const isDarkMode = computed(() => !!store.state.isDarkMode);
const qrBackground = computed(() => (isDarkMode.value ? "#334155" : "#ffffff"));

const creditAmount = ref(null);
const qrCodeValue = ref("");
const autofocus = ref(null);
const copiedKey = ref("");
let copiedTimer = null;

const copyValue = async (key, value) => {
  const text = String(value || "").trim();
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const input = document.createElement("textarea");
    input.value = text;
    input.setAttribute("readonly", "");
    input.style.position = "fixed";
    input.style.left = "-9999px";
    document.body.appendChild(input);
    input.select();
    document.execCommand("copy");
    document.body.removeChild(input);
  }
  copiedKey.value = key;
  clearTimeout(copiedTimer);
  copiedTimer = setTimeout(() => {
    copiedKey.value = "";
  }, 1600);
};

const formattedIban = computed(() => {
  const raw = String(props.paymentData?.BankAccountIBAN || "").replace(/\s+/g, "");
  return raw.replace(/(.{4})/g, "$1 ").trim();
});

const minAmount = 1;
const maxAmount = 1000000;
const stepAmount = 10;
const jumpStep = 100;

const amountNum = computed(() => {
  const n = Number(creditAmount.value);
  return Number.isFinite(n) ? n : 0;
});

const setCreditAmount = (value, inputEl) => {
  if (value === "" || value === null) {
    creditAmount.value = null;
    if (inputEl) inputEl.value = "";
    return;
  }
  const n = Number(String(value).replace(",", "."));
  if (!Number.isFinite(n) || n < 0) {
    creditAmount.value = null;
    if (inputEl) inputEl.value = "";
    return;
  }
  const next = n > maxAmount ? String(maxAmount) : value;
  creditAmount.value = next;
  if (inputEl && inputEl.value !== String(next)) inputEl.value = String(next);
  qrCodeValue.value = props.paymentData.QRCode.replace("##AMOUNT##", next);
};

const onAmountInput = (event) => {
  setCreditAmount(event.target.value, event.target);
};

const onAmountBlur = (event) => {
  setCreditAmount(event.target.value, event.target);
};

const nudge = (delta) => {
  const next = Math.min(maxAmount, Math.max(minAmount, amountNum.value + delta));
  setCreditAmount(String(next));
};

const focusAmount = () => {
  nextTick(() => {
    autofocus.value?.focus();
    autofocus.value?.scrollIntoView({ block: "nearest" });
  });
};

onMounted(() => {
  nextTick(() => {
    autofocus.value?.focus();
  });
});
</script>

<style scoped>
.topup {
  padding: 0 0.35rem 0.35rem;
}

.pay-lead {
  margin: 0 0.1rem 0.85rem;
  font-size: 0.82rem;
  line-height: 1.45;
  font-weight: 600;
  color: #475569;
  text-align: center;
}

.topup-dark .pay-lead {
  color: #E2E8F0;
}

.pay-lead-accent {
  color: #EA580C;
  font-weight: 800;
  white-space: nowrap;
}

.topup-dark .pay-lead-accent {
  color: #FF8A4C;
}

.pay-lead-link {
  border: 0;
  padding: 0;
  background: none;
  color: #EA580C;
  font-weight: 800;
  text-decoration: underline;
  text-underline-offset: 0.15em;
  cursor: pointer;
}

.topup-dark .pay-lead-link {
  color: #FF8A4C;
}

.pay-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.55rem;
  margin-bottom: 0.75rem;
}

.pay-chip {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  padding: 0.55rem 0.7rem;
  border-radius: 0.75rem;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
}

.topup-dark .pay-chip {
  background: #475569;
  border-color: #64748B;
}

.pay-chip-iban,
.pay-chip-amount {
  grid-column: 1 / -1;
}

.pay-value-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.4rem;
}

.pay-value {
  font-size: 1.02rem;
  font-weight: 800;
  line-height: 1.25;
  color: #0F172A;
  word-break: break-word;
}

.copy-btn {
  flex-shrink: 0;
  margin-top: 0.1rem;
  border: 0;
  background: transparent;
  color: #94A3B8;
  padding: 0;
  line-height: 1;
  cursor: pointer;
}

.topup-dark .copy-btn {
  color: #E2E8F0;
}

.copy-btn:hover {
  color: #FF8A4C;
}

.topup-dark .pay-value {
  color: #F8FAFC;
}

.pay-value-accent {
  color: #EA580C;
}

.topup-dark .pay-value-accent {
  color: #FF8A4C;
}

.pay-chip-amount .pay-stepper {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  width: 100%;
}

.pay-stepper-btn {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border: 1.5px solid #CBD5E1;
  border-radius: 50%;
  background: #fff;
  color: #475569;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  font-size: 0.72rem;
  line-height: 1;
}

.pay-stepper-btn:hover:not(:disabled) {
  color: #FF8A4C;
  border-color: #FF8A4C;
}

.pay-stepper-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

.pay-stepper-btn-jump {
  font-size: 0.82rem;
  font-weight: 800;
}

.pay-stepper-btn-jump :deep(svg) {
  font-size: 0.82rem;
}

.jump-num {
  font-size: 0.52rem;
  font-weight: 800;
  letter-spacing: 0.01em;
  line-height: 1;
  margin-top: 0.02rem;
}

.topup-dark .pay-stepper-btn {
  background: #334155;
  border-color: #64748B;
  color: #E2E8F0;
}

.pay-chip-amount .pay-amount-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 34px;
  border: 1.5px solid #E2E8F0;
  border-radius: 0.65rem;
  background: #fff;
  box-shadow: none;
  font-size: 0.95rem;
  font-weight: 800;
  color: #EA580C;
  padding: 0 0.45rem;
  outline: none;
  text-align: center;
  -moz-appearance: textfield;
}

.topup-dark .pay-chip-amount .pay-amount-input {
  background: #334155;
  border-color: #64748B;
  color: #FF8A4C;
}

.pay-chip-amount .pay-amount-input:focus {
  border-color: #FF8A4C;
}

.pay-chip-amount .pay-amount-input::placeholder {
  color: #94A3B8;
  font-weight: 700;
  font-size: 0.85rem;
}

.topup-dark .pay-chip-amount .pay-amount-input::placeholder {
  color: #CBD5E1;
}

.pay-chip-amount .pay-amount-input::-webkit-outer-spin-button,
.pay-chip-amount .pay-amount-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.pay-label {
  margin-top: 0.12rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #475569;
}

.topup-dark .pay-label {
  color: #F1F5F9;
}

.qr-area {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.qr-hint {
  margin: 0;
  padding: 1.5rem;
  text-align: center;
  font-size: 0.8rem;
  font-weight: 600;
  color: #94A3B8;
}

.topup-dark .qr-hint {
  color: #E2E8F0;
}

.qr-area :deep(canvas),
.qr-area :deep(svg) {
  display: inline-block;
  vertical-align: middle;
  background: transparent;
}
</style>
